import { useEffect, useState, useCallback } from 'react';
import { API_BASE } from '../utils/apiClient';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
  Pagination,
} from '@mui/material';
import { useNotify, useRefresh, usePermissions } from 'react-admin';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import { calculatePageCount } from '../utils';

interface Entity {
  id: string;
  entity_type: string;
  category: string | null;
  display_name: string;
  attributes: Record<string, unknown>;
  verification_status: string;
  verification_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface QueueResponse {
  entities: Entity[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

export const VerificationQueue = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();

  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, perPage: 25 });

  // Filters
  const [entityType, setEntityType] = useState<string>('');
  const [category, setCategory] = useState<string>('');

  // Selection for bulk actions
  const [selected, setSelected] = useState<string[]>([]);

  // Dialogs
  const [viewEntity, setViewEntity] = useState<Entity | null>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'verify' | 'reject' | 'bulk-verify';
    entityId?: string;
  }>({ open: false, action: 'verify' });
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: String(pagination.perPage),
        offset: String((pagination.page - 1) * pagination.perPage),
      });

      if (entityType) params.append('entity_type', entityType);
      if (category) params.append('category', category);

      const response = await fetch(`${API_BASE}/admin/api/verification/queue?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch verification queue');
      }

      const data: QueueResponse = await response.json();
      setEntities(data.entities);
      setPagination((prev) => ({ ...prev, total: data.pagination.total }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.perPage, entityType, category]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleVerify = async (entityId: string, notes: string) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/api/entities/${entityId}/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to verify entity');
      }

      notify('Entity verified successfully', { type: 'success' });
      setActionDialog({ open: false, action: 'verify' });
      setActionNotes('');
      fetchQueue();
      refresh();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to verify entity', { type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (entityId: string, notes: string) => {
    if (!notes.trim()) {
      notify('Notes are required when rejecting', { type: 'error' });
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/api/entities/${entityId}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to reject entity');
      }

      notify('Entity rejected', { type: 'success' });
      setActionDialog({ open: false, action: 'reject' });
      setActionNotes('');
      fetchQueue();
      refresh();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to reject entity', { type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkVerify = async (notes: string) => {
    if (selected.length === 0) {
      notify('No entities selected', { type: 'error' });
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/api/verification/bulk-verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityIds: selected, notes }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to bulk verify entities');
      }

      const data = await response.json();
      notify(`${data.verified} entities verified successfully`, { type: 'success' });
      setActionDialog({ open: false, action: 'bulk-verify' });
      setActionNotes('');
      setSelected([]);
      fetchQueue();
      refresh();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to bulk verify entities', { type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(entities.map((e) => e.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const canVerify =
    permissions?.role === 'admin' ||
    permissions?.role === 'super_admin' ||
    permissions?.permissions?.includes('entities:verify');

  if (!canVerify) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to access the verification queue.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Verification Queue</Typography>
        <Box display="flex" gap={1}>
          {selected.length > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setActionDialog({ open: true, action: 'bulk-verify' })}
            >
              Verify Selected ({selected.length})
            </Button>
          )}
          <IconButton onClick={fetchQueue}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={entityType}
                  label="Entity Type"
                  onChange={(e) => {
                    setEntityType(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="cricket_player">Cricket Player</MenuItem>
                  <MenuItem value="movie">Movie</MenuItem>
                  <MenuItem value="movie_song">Movie Song</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Category"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="textSecondary">
                {pagination.total} entities pending review
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Queue Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : entities.length === 0 ? (
        <Alert severity="info">No entities pending verification</Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < entities.length}
                      checked={entities.length > 0 && selected.length === entities.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entities.map((entity) => (
                  <TableRow key={entity.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(entity.id)}
                        onChange={() => handleSelectOne(entity.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {entity.display_name}
                      </Typography>
                      {entity.verification_notes && (
                        <Typography variant="caption" color="textSecondary">
                          Note: {entity.verification_notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={entity.entity_type.replace('_', ' ')}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>{entity.category || '-'}</TableCell>
                    <TableCell>
                      {new Date(entity.updated_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => setViewEntity(entity)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Approve">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() =>
                            setActionDialog({ open: true, action: 'verify', entityId: entity.id })
                          }
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setActionDialog({ open: true, action: 'reject', entityId: entity.id })
                          }
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={calculatePageCount(pagination.total, pagination.perPage)}
              page={pagination.page}
              onChange={(_, page) => setPagination((prev) => ({ ...prev, page }))}
            />
          </Box>
        </>
      )}

      {/* View Entity Dialog */}
      <Dialog open={!!viewEntity} onClose={() => setViewEntity(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewEntity?.display_name}
          <Chip
            label={viewEntity?.entity_type.replace('_', ' ')}
            size="small"
            sx={{ ml: 2, textTransform: 'capitalize' }}
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Attributes
            </Typography>
            <Paper
              sx={{
                p: 2,
                backgroundColor: '#f5f5f5',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflow: 'auto',
                maxHeight: 400,
              }}
            >
              <pre style={{ margin: 0 }}>
                {JSON.stringify(viewEntity?.attributes, null, 2)}
              </pre>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewEntity(null)}>Close</Button>
          <Button
            color="error"
            onClick={() => {
              setViewEntity(null);
              setActionDialog({ open: true, action: 'reject', entityId: viewEntity?.id });
            }}
          >
            Reject
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => {
              setViewEntity(null);
              setActionDialog({ open: true, action: 'verify', entityId: viewEntity?.id });
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => {
          setActionDialog({ open: false, action: 'verify' });
          setActionNotes('');
        }}
      >
        <DialogTitle>
          {actionDialog.action === 'verify' && 'Verify Entity'}
          {actionDialog.action === 'reject' && 'Reject Entity'}
          {actionDialog.action === 'bulk-verify' && `Bulk Verify ${selected.length} Entities`}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={actionDialog.action === 'reject' ? 'Rejection Notes (Required)' : 'Notes (Optional)'}
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            sx={{ mt: 2 }}
            required={actionDialog.action === 'reject'}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActionDialog({ open: false, action: 'verify' });
              setActionNotes('');
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            color={actionDialog.action === 'reject' ? 'error' : 'success'}
            variant="contained"
            disabled={actionLoading || (actionDialog.action === 'reject' && !actionNotes.trim())}
            onClick={() => {
              if (actionDialog.action === 'verify' && actionDialog.entityId) {
                handleVerify(actionDialog.entityId, actionNotes);
              } else if (actionDialog.action === 'reject' && actionDialog.entityId) {
                handleReject(actionDialog.entityId, actionNotes);
              } else if (actionDialog.action === 'bulk-verify') {
                handleBulkVerify(actionNotes);
              }
            }}
          >
            {actionLoading ? (
              <CircularProgress size={24} />
            ) : actionDialog.action === 'reject' ? (
              'Reject'
            ) : (
              'Verify'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
